import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Alert,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

import {
  type ShiftDto,
  type ShiftScope,
  type BranchWorkingHour,
  getBranchShifts,
} from 'src/api/branch_api_data';
import {
  type AssignmentDto,
  type AssignmentCreateDto,
  type PersonalShiftDto,
  getEmployeeShiftAssignments,
  getActiveAssignment,
  createAssignment,
  deleteAssignment,
  setPersonalShift,
} from 'src/api/api_data';

import {
  WorkingHoursGrid,
  getDefaultWorkingHours,
  normalizeWorkingHours,
  validateWorkingHours,
} from 'src/sections/branch/components/WorkingHoursGrid';

// ----------------------------------------------------------------------

interface EmployeeShiftTabProps {
  employeeId: string;
  branchId?: string;
}

const SCOPE_LABELS: Record<ShiftScope, string> = {
  SHARED: 'Paylaşılan',
  PERSONAL: 'Kişisel',
};

const WORKING_HOURS_ERROR = 'Açık günlerde açılış ve kapanış saati farklı olmalıdır.';

const today = () => new Date().toISOString().slice(0, 10);

const formatDate = (value: string | null): string => {
  if (!value) return 'Devam ediyor';
  try {
    return new Date(value).toLocaleDateString('tr-TR');
  } catch {
    return value;
  }
};

// ----- Atama dialog state -----
interface AssignFormState {
  open: boolean;
  shiftId: string;
  effectiveFrom: string;
  effectiveTo: string;
  submitting: boolean;
  error: string;
}

const emptyAssignForm: AssignFormState = {
  open: false,
  shiftId: '',
  effectiveFrom: today(),
  effectiveTo: '',
  submitting: false,
  error: '',
};

// ----- Kişiye özel saat dialog state -----
interface PersonalFormState {
  open: boolean;
  name: string;
  workingHours: BranchWorkingHour[];
  submitting: boolean;
  error: string;
}

const emptyPersonalForm: PersonalFormState = {
  open: false,
  name: '',
  workingHours: getDefaultWorkingHours(),
  submitting: false,
  error: '',
};

interface DeleteState {
  open: boolean;
  assignment: AssignmentDto | null;
  submitting: boolean;
}

const EmployeeShiftTab: React.FC<EmployeeShiftTabProps> = ({ employeeId, branchId }) => {
  const [active, setActive] = useState<AssignmentDto | null>(null);
  const [history, setHistory] = useState<AssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const [shiftOptions, setShiftOptions] = useState<ShiftDto[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);

  const [assignForm, setAssignForm] = useState<AssignFormState>(emptyAssignForm);
  const [personalForm, setPersonalForm] = useState<PersonalFormState>(emptyPersonalForm);
  const [deleteState, setDeleteState] = useState<DeleteState>({
    open: false,
    assignment: null,
    submitting: false,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [activeData, historyData] = await Promise.all([
        getActiveAssignment(employeeId),
        getEmployeeShiftAssignments(employeeId),
      ]);
      setActive(activeData);
      setHistory(Array.isArray(historyData) ? historyData : []);
    } catch (err) {
      console.error('Vardiya atamaları yüklenemedi:', err);
      setError('Vardiya bilgileri yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ----- Vardiya Ata -----

  const openAssign = async () => {
    setAssignForm({ ...emptyAssignForm, open: true });
    if (!branchId) return;
    try {
      setShiftsLoading(true);
      const data = await getBranchShifts(branchId);
      setShiftOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Vardiyalar yüklenemedi:', err);
      toast.error('Vardiya listesi yüklenemedi.');
    } finally {
      setShiftsLoading(false);
    }
  };

  const closeAssign = () => {
    if (assignForm.submitting) return;
    setAssignForm((prev) => ({ ...prev, open: false }));
  };

  const handleShiftChange = (event: SelectChangeEvent<string>) => {
    setAssignForm((prev) => ({ ...prev, shiftId: event.target.value }));
  };

  const handleAssignSubmit = async () => {
    if (!assignForm.shiftId) {
      setAssignForm((prev) => ({ ...prev, error: 'Lütfen bir vardiya seçin.' }));
      return;
    }
    if (!assignForm.effectiveFrom) {
      setAssignForm((prev) => ({ ...prev, error: 'Başlangıç tarihi zorunludur.' }));
      return;
    }
    if (assignForm.effectiveTo && assignForm.effectiveTo < assignForm.effectiveFrom) {
      setAssignForm((prev) => ({
        ...prev,
        error: 'Bitiş tarihi başlangıç tarihinden önce olamaz.',
      }));
      return;
    }

    const dto: AssignmentCreateDto = {
      shiftId: assignForm.shiftId,
      effectiveFrom: assignForm.effectiveFrom,
      effectiveTo: assignForm.effectiveTo || null,
    };

    try {
      setAssignForm((prev) => ({ ...prev, submitting: true, error: '' }));
      await createAssignment(employeeId, dto);
      toast.success('Vardiya atandı.');
      setAssignForm((prev) => ({ ...prev, open: false, submitting: false }));
      await loadData();
    } catch (err: any) {
      console.error('Vardiya atanamadı:', err);
      // Backend ErrorResponse: katmanlı kod "errorRef" alaninda doner.
      const ref = err?.response?.data?.errorRef;
      const message =
        ref === 'A071'
          ? 'Bu tarih aralığı mevcut bir atama ile çakışıyor.'
          : 'Vardiya atanırken bir hata oluştu. Lütfen tekrar deneyin.';
      setAssignForm((prev) => ({ ...prev, submitting: false, error: message }));
    }
  };

  // ----- Kişiye Özel Saat -----

  const openPersonal = () => {
    setPersonalForm({
      ...emptyPersonalForm,
      open: true,
      workingHours:
        active?.shift?.scope === 'PERSONAL'
          ? normalizeWorkingHours(active.shift.workingHours)
          : getDefaultWorkingHours(),
      name: active?.shift?.scope === 'PERSONAL' ? active.shift.name || '' : '',
    });
  };

  const closePersonal = () => {
    if (personalForm.submitting) return;
    setPersonalForm((prev) => ({ ...prev, open: false }));
  };

  const handlePersonalHoursChange = (workingHours: BranchWorkingHour[]) => {
    setPersonalForm((prev) => ({ ...prev, workingHours }));
  };

  const handlePersonalSubmit = async () => {
    if (!validateWorkingHours(personalForm.workingHours)) {
      setPersonalForm((prev) => ({ ...prev, error: WORKING_HOURS_ERROR }));
      return;
    }

    const dto: PersonalShiftDto = {
      name: personalForm.name.trim() || undefined,
      workingHours: personalForm.workingHours,
    };

    try {
      setPersonalForm((prev) => ({ ...prev, submitting: true, error: '' }));
      await setPersonalShift(employeeId, dto);
      toast.success('Kişiye özel saat kaydedildi.');
      setPersonalForm((prev) => ({ ...prev, open: false, submitting: false }));
      await loadData();
    } catch (err) {
      console.error('Kişiye özel saat kaydedilemedi:', err);
      setPersonalForm((prev) => ({
        ...prev,
        submitting: false,
        error: 'Kişiye özel saat kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.',
      }));
    }
  };

  // ----- Silme -----

  const openDelete = (assignment: AssignmentDto) => {
    setDeleteState({ open: true, assignment, submitting: false });
  };

  const closeDelete = () => {
    if (deleteState.submitting) return;
    setDeleteState({ open: false, assignment: null, submitting: false });
  };

  const handleDelete = async () => {
    if (!deleteState.assignment) return;
    try {
      setDeleteState((prev) => ({ ...prev, submitting: true }));
      await deleteAssignment(employeeId, deleteState.assignment.id);
      toast.success('Atama kaldırıldı.');
      setDeleteState({ open: false, assignment: null, submitting: false });
      await loadData();
    } catch (err) {
      console.error('Atama kaldırılamadı:', err);
      toast.error('Atama kaldırılırken bir hata oluştu. Lütfen tekrar deneyin.');
      setDeleteState((prev) => ({ ...prev, submitting: false }));
    }
  };

  // ----- Render -----

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h6">Vardiya</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Yenile">
            <IconButton onClick={loadData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<AccessTimeIcon />}
            onClick={openPersonal}
          >
            Kişiye Özel Saat
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAssign}>
            Vardiya Ata
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Mevcut atama */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Mevcut Atama
          </Typography>
          {active ? (
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                <ScheduleIcon color="action" />
                <Typography variant="subtitle1">
                  {active.shift.isDefault && !active.shift.name
                    ? 'Varsayılan vardiya'
                    : active.shift.name}
                </Typography>
                <Chip
                  size="small"
                  label={SCOPE_LABELS[active.shift.scope]}
                  color={active.shift.scope === 'SHARED' ? 'primary' : 'default'}
                  variant="outlined"
                />
                {active.shift.isDefault && (
                  <Chip size="small" label="Varsayılan" color="success" />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {`${formatDate(active.effectiveFrom)} - ${formatDate(active.effectiveTo)}`}
              </Typography>
              <WorkingHoursGrid
                value={active.shift.workingHours}
                onChange={() => {}}
                disabled
              />
            </Stack>
          ) : (
            <Alert severity="info">Atama yok — şube varsayılanı geçerli.</Alert>
          )}
        </CardContent>
      </Card>

      {/* Atama geçmişi */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Atama Geçmişi
          </Typography>
          {history.length === 0 ? (
            <Typography color="text.secondary">Bu çalışan için atama bulunmuyor.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vardiya</TableCell>
                  <TableCell>Kapsam</TableCell>
                  <TableCell>Başlangıç</TableCell>
                  <TableCell>Bitiş</TableCell>
                  <TableCell align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {row.shift.isDefault && !row.shift.name
                        ? 'Varsayılan vardiya'
                        : row.shift.name}
                    </TableCell>
                    <TableCell>{SCOPE_LABELS[row.shift.scope]}</TableCell>
                    <TableCell>{formatDate(row.effectiveFrom)}</TableCell>
                    <TableCell>{formatDate(row.effectiveTo)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Kaldır">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDelete(row)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Vardiya Ata Dialog */}
      <Dialog open={assignForm.open} onClose={closeAssign} maxWidth="sm" fullWidth>
        <DialogTitle>Vardiya Ata</DialogTitle>
        {assignForm.submitting && <LinearProgress />}
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {assignForm.error && <Alert severity="error">{assignForm.error}</Alert>}
            {!branchId && (
              <Alert severity="warning">
                Şube bilgisi bulunamadığı için vardiya listesi yüklenemiyor.
              </Alert>
            )}

            <FormControl fullWidth disabled={assignForm.submitting || shiftsLoading}>
              <InputLabel id="assign-shift-label">Vardiya</InputLabel>
              <Select
                labelId="assign-shift-label"
                label="Vardiya"
                value={assignForm.shiftId}
                onChange={handleShiftChange}
              >
                {shiftOptions.map((shift) => (
                  <MenuItem key={shift.id} value={shift.id}>
                    {shift.isDefault && !shift.name ? 'Varsayılan vardiya' : shift.name}
                    {` (${SCOPE_LABELS[shift.scope]})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Başlangıç"
              type="date"
              value={assignForm.effectiveFrom}
              onChange={(e) =>
                setAssignForm((prev) => ({ ...prev, effectiveFrom: e.target.value }))
              }
              disabled={assignForm.submitting}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Bitiş (opsiyonel)"
              type="date"
              value={assignForm.effectiveTo}
              onChange={(e) =>
                setAssignForm((prev) => ({ ...prev, effectiveTo: e.target.value }))
              }
              disabled={assignForm.submitting}
              InputLabelProps={{ shrink: true }}
              helperText="Boş bırakılırsa atama süresizdir."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeAssign} disabled={assignForm.submitting}>
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignSubmit}
            disabled={assignForm.submitting}
          >
            {assignForm.submitting ? 'Atanıyor...' : 'Ata'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kişiye Özel Saat Dialog */}
      <Dialog open={personalForm.open} onClose={closePersonal} maxWidth="md" fullWidth>
        <DialogTitle>Kişiye Özel Saat</DialogTitle>
        {personalForm.submitting && <LinearProgress />}
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {personalForm.error && <Alert severity="error">{personalForm.error}</Alert>}

            <TextField
              label="Ad (opsiyonel)"
              value={personalForm.name}
              onChange={(e) =>
                setPersonalForm((prev) => ({ ...prev, name: e.target.value }))
              }
              disabled={personalForm.submitting}
              fullWidth
            />

            <WorkingHoursGrid
              value={personalForm.workingHours}
              onChange={handlePersonalHoursChange}
              disabled={personalForm.submitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closePersonal} disabled={personalForm.submitting}>
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handlePersonalSubmit}
            disabled={personalForm.submitting}
          >
            {personalForm.submitting ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Onay Dialog */}
      <Dialog open={deleteState.open} onClose={closeDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Atamayı Kaldır</DialogTitle>
        {deleteState.submitting && <LinearProgress />}
        <DialogContent>
          <Typography>
            Bu vardiya atamasını kaldırmak istediğinize emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDelete} disabled={deleteState.submitting}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteState.submitting}
          >
            {deleteState.submitting ? 'Kaldırılıyor...' : 'Kaldır'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeShiftTab;
